import * as React from 'react'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'

import { InfoIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { IPermitTypeField, LocationType } from '~/client/graph/types.generated'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import locationTypesList from '~/client/src/shared/constants/locationTypesList'
import {
  equipmentPermitFieldTypes,
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { useLocationFiltering } from '~/client/src/shared/hooks/useLocationFiltering'
import useMaterialResources from '~/client/src/shared/hooks/useMaterialResources'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import InitialState from '~/client/src/shared/stores/InitialState'

import FieldSectionHeader from '../../../FieldSectionHeader/FieldSectionHeader'
import { DisabledResourceIdsContext } from '../../../FormTypeConfigurator/DisabledResourceIdsContext'
import NewRuleButton from '../../../RuleComponents/NewRuleButton'
import ConditionalRule from './components/ConditionalRule'
import MaterialDependentFields from './components/MaterialDependentFields'
import NewRuleForm from './components/NewRuleForm'
import DependentFieldsStore from './DependentFields.store'
import { useFieldOptions } from './hooks/useFieldOptions'
import { useHandlers } from './hooks/useHandlers'
import { useRuleManagement } from './hooks/useRuleManagement'
import IDependentFieldsConfiguratorProps from './types/IDependentFieldsConfiguratorProps'

interface IProps extends IDependentFieldsConfiguratorProps {
  field: IPermitTypeField

  lbsLocationId?: string
  renderHeader?: (
    startCreatingNewRule: () => void,
    context: { rulesCount: number; isExpanded: boolean; onToggle: () => void },
  ) => JSX.Element
  renderNewRuleButton?: (onClick: () => void) => JSX.Element

  locationAttributesStore?: LocationAttributesStore
  state?: InitialState
}

function DependentFieldsConfigurator({
  field,
  lbsLocationId,
  allConditionalFields,
  depthLevel = 1,
  renderHeader,
  renderNewRuleButton,
  locationAttributesStore,
  state,
  addNewConditionalField,
  removeConditionalField,
  updateConditionalField,
  tableConfiguratorRenderer,
}: IProps) {
  const [store] = useState(() => new DependentFieldsStore())

  const isLocationFieldType = locationPermitFieldTypes.includes(field.type)
  const isEquipmentFieldType = equipmentPermitFieldTypes.includes(field.type)
  const isMaterialResourceFieldType = materialResourcePermitFieldTypes.includes(
    field.type,
  )

  const projectId = state?.activeProject?.id
  const { records: materialResourceRecords } = useMaterialResources({
    projectId: isMaterialResourceFieldType ? projectId : undefined,
  })

  const { restrictedLocationIds, allowedLocationIds } = useLocationFiltering(
    field.isLBSLogicEnabled,
    isLocationFieldType,
    isEquipmentFieldType,
    lbsLocationId,
    field.filterOptions || [],
    locationAttributesStore,
  )

  // Hide resources disabled for the active workflow type (not bookable / wrong
  // type), uniformly with the other resource selectors. The field's allow-list
  // is still applied by useLocationFiltering above.
  const disabledResourceIds = useContext(DisabledResourceIdsContext)
  const effectiveRestrictedLocationIds = useMemo(
    () => [...restrictedLocationIds, ...disabledResourceIds],
    [restrictedLocationIds, disabledResourceIds],
  )

  const restrictedLocationTypes = useMemo((): LocationType[] => {
    if (!isLocationFieldType) return []
    return isEquipmentFieldType
      ? locationTypesList.filter(lt => lt !== LocationType.OffloadingEquipment)
      : locationTypesList.filter(t => t === LocationType.OffloadingEquipment)
  }, [isLocationFieldType, isEquipmentFieldType])

  const { selectOptions, availableSelectOptions, restrictedFieldTypes } =
    useFieldOptions(
      field,
      depthLevel,
      allConditionalFields,
      allowedLocationIds,
      materialResourceRecords,
    )

  const {
    allRules,
    allUsedLocationIds,
    availableRules,
    inactiveRules,
    getRuleDisplayName,
  } = useRuleManagement(
    field,
    isLocationFieldType,
    allConditionalFields,
    selectOptions,
    locationAttributesStore,
    isMaterialResourceFieldType,
    materialResourceRecords,
  )

  const dependentFieldsTooltip = useMemo(
    () => (
      <div>
        <div className="mb8">
          <strong>
            {Localization.translator.dependentFieldsTooltip.title}
          </strong>
        </div>
        <div>{Localization.translator.dependentFieldsTooltip.description}</div>
      </div>
    ),
    [Localization.state.currentLanguage],
  )

  // Reset if expanded rule no longer exists (render-time state adjustment)
  if (
    store.expandedRuleKey &&
    !allRules.some(rule => rule.key === store.expandedRuleKey)
  ) {
    store.resetAll()
  }

  // Collapse on external save only (not on internal edits like adding fields).
  const isInternalEditRef = useRef(false)
  const prevConditionalFieldsRef = useRef(allConditionalFields)
  if (prevConditionalFieldsRef.current !== allConditionalFields) {
    prevConditionalFieldsRef.current = allConditionalFields
    if (
      !isInternalEditRef.current &&
      (store.expandedRuleKey || store.isCreatingNewRule)
    ) {
      store.resetAll()
    }
    isInternalEditRef.current = false
  }

  const flagAsInternal = useCallback(
    <T extends (...args: never[]) => void>(fn: T) =>
      ((...args: Parameters<T>) => {
        isInternalEditRef.current = true
        fn(...args)
      }) as unknown as T,
    [],
  )

  const wrappedCallbacks = useMemo(
    () => ({
      addNew: flagAsInternal(addNewConditionalField),
      update: flagAsInternal(updateConditionalField),
      remove: flagAsInternal(removeConditionalField),
    }),
    [
      flagAsInternal,
      addNewConditionalField,
      updateConditionalField,
      removeConditionalField,
    ],
  )

  const handlers = useHandlers({
    field,
    allRules,
    isLBSToggleEnabled: isLocationFieldType,
    addNewConditionalField: wrappedCallbacks.addNew,
    updateConditionalField: wrappedCallbacks.update,
    removeConditionalField: wrappedCallbacks.remove,
    onToggleRule: store.toggleRule,
  })

  const mainContainerProps = useMemo(
    () => ({
      allConditionalFields,
      depthLevel,
      tableConfiguratorRenderer,
      addNewConditionalField: wrappedCallbacks.addNew,
      updateConditionalField: wrappedCallbacks.update,
      removeConditionalField: wrappedCallbacks.remove,
    }),
    [
      allConditionalFields,
      depthLevel,
      tableConfiguratorRenderer,
      wrappedCallbacks,
    ],
  )

  const isMaterialField = materialPermitFieldTypes.includes(field.type)

  const pickerRestrictedIds = useMemo(() => {
    return [...effectiveRestrictedLocationIds, ...allUsedLocationIds]
  }, [effectiveRestrictedLocationIds, allUsedLocationIds])

  const usesPickerFlow = isLocationFieldType || isMaterialResourceFieldType

  const renderRule = (rule: (typeof allRules)[number]) => (
    <ConditionalRule
      key={rule.key}
      rule={rule}
      field={field}
      isExpanded={store.expandedRuleKey === rule.key}
      isLBSToggleShown={isLocationFieldType}
      usesPickerFlow={usesPickerFlow}
      depthLevel={depthLevel}
      restrictedFieldTypes={restrictedFieldTypes}
      restrictedLocationIds={effectiveRestrictedLocationIds}
      allUsedLocationIds={allUsedLocationIds}
      restrictedLocationTypes={restrictedLocationTypes}
      handlers={handlers}
      mainContainerProps={mainContainerProps}
      onToggleRule={store.toggleRule}
      getRuleDisplayName={getRuleDisplayName}
      tableConfiguratorRenderer={tableConfiguratorRenderer}
    />
  )

  return (
    <div
      className={classList({
        'pl10 pr20': !renderHeader,
        ml10: !renderHeader && depthLevel === 1,
        'ml38 mr8': !renderHeader && depthLevel > 1,
        'tw:pl-6': !!renderHeader && depthLevel === 1,
        'tw:pl-14 tw:pr-2': !!renderHeader && depthLevel > 1,
      })}
    >
      {!isMaterialField && (
        <>
          {renderHeader ? (
            renderHeader(store.startCreatingNewRule, {
              rulesCount: allRules.length,
              isExpanded: !store.areRulesHidden,
              onToggle: store.toggleRules,
            })
          ) : (
            <div className="row y-start pt5 pb10">
              <FieldSectionHeader
                text={Localization.translator.conditionalFields}
                isExpanded={!store.areRulesHidden}
                onToggle={store.toggleRules}
                isIconHidden={!allRules.length}
              />
              <TooltipWrapper
                className="row no-grow ml8"
                content={dependentFieldsTooltip}
              >
                <InfoIcon size={16} className="tw:text-muted-foreground" />
              </TooltipWrapper>
              {!store.isCreatingNewRule && (
                <NewRuleButton onClick={store.startCreatingNewRule} />
              )}
            </div>
          )}

          {!store.areRulesHidden && (
            <div className="pt4">
              {availableRules.map(renderRule)}
              {!!availableRules.length &&
                !store.isCreatingNewRule &&
                (renderNewRuleButton ? (
                  renderNewRuleButton(store.startCreatingNewRule)
                ) : (
                  <NewRuleButton onClick={store.startCreatingNewRule} />
                ))}
              {store.isCreatingNewRule && (
                <NewRuleForm
                  field={field}
                  usesPickerFlow={usesPickerFlow}
                  availableSelectOptions={availableSelectOptions}
                  restrictedLocationIds={pickerRestrictedIds}
                  restrictedLocationTypes={restrictedLocationTypes}
                  restrictedFieldTypes={restrictedFieldTypes}
                  onCancel={store.cancelNewRule}
                  onNewRuleValueSelect={handlers.handleNewRuleValueSelect}
                  onLocationApply={(ids, fieldType) => {
                    handlers.handleLocationApply(ids, fieldType)
                    store.completeRuleCreation()
                  }}
                />
              )}
              {!!inactiveRules.length && (
                <>
                  <FieldSectionHeader
                    className="py10 w-fit-content"
                    text={Localization.translator.inactive}
                    isExpanded={store.areInactiveRulesShown}
                    onToggle={store.toggleInactiveRules}
                  />
                  {store.areInactiveRulesShown && (
                    <div className="opacity7">
                      {inactiveRules.map(renderRule)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
      {isMaterialField && (
        <MaterialDependentFields
          rule={allRules?.[0]}
          isLBSToggleShown={isLocationFieldType}
          depthLevel={depthLevel}
          restrictedFieldTypes={restrictedFieldTypes}
          mainContainerProps={mainContainerProps}
          handlers={handlers}
          tableConfiguratorRenderer={tableConfiguratorRenderer}
        />
      )}
    </div>
  )
}

export default inject(
  'locationAttributesStore',
  'state',
)(observer(DependentFieldsConfigurator))
